import { BodySmall, Flex } from '@taxbit-private/cosmic'
import { useCosmicLocalizationContext } from '@taxbit-private/cosmic-localization'

import { isActionAllowed } from 'pages/vouchers/hooks/useVoucherPermissions'

import {
  AccountingMappings,
  ErpInboundOutboundActivity,
  MappingAccountType,
  MappingSplitDistributionType,
  MappingType,
} from 'api/types/accountingMappingApiTypes'
import { Transaction } from 'api/types/transaction/transactionApiTypes'
import { User } from 'api/types/userApiTypes'
import { VoucherListResponse } from 'api/types/voucher/voucherApiSchemas'
import { VoucherAction } from 'api/types/voucher/voucherEnums'
import { Voucher } from 'api/types/voucher/voucherSchemas'
import TokenAvatar from 'components/token-avatar/TokenAvatar'
import { isEnumMember } from 'utils/enumUtils'
import { truncateToLength } from 'utils/transactions/TransactionsUtils'

import { HydratedTransaction } from './TransactionsTypes'

// Helper function to check if an object is empty
function isEmpty(obj: Record<string, unknown>): boolean {
  return Object.keys(obj).length === 0
}

// Helper function to convert numbers to numbers
function toNumber(value: unknown): number {
  if (value === null || value === undefined) {
    return 0
  }
  const parsed = Number(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

export function getWalletQualifierFromTxn(
  transaction: HydratedTransaction,
  variant: 'to' | 'from'
) {
  const addr = variant === 'from' ? transaction.fromAddress.addr : transaction.toAddress.addr
  const chain = transaction.dataSource.source

  return `${addr}@${chain}`
}
export function getHierarchicalMapping(
  transaction: HydratedTransaction,
  mappings: AccountingMappings
) {
  const defaultMapping = { ...mappings.default }

  if (isEmpty(defaultMapping)) {
    return defaultMapping
  }

  const fromWalletMapping = Object.fromEntries(
    Object.entries(mappings.wallet?.[getWalletQualifierFromTxn(transaction, 'from')] || {}).filter(
      ([k]) => {
        return (
          isEnumMember(MappingAccountType, k) &&
          [
            MappingAccountType.Asset,
            MappingAccountType.OutboundActivity,
            MappingAccountType.Fee,
            MappingAccountType.Class,
            MappingAccountType.Location,
          ].includes(k)
        )
      }
    )
  )

  const toWalletMapping = Object.fromEntries(
    Object.entries(mappings.wallet?.[getWalletQualifierFromTxn(transaction, 'to')] || {}).filter(
      ([k]) => {
        return (
          isEnumMember(MappingAccountType, k) &&
          [
            MappingAccountType.Asset,
            MappingAccountType.InboundActivity,
            MappingAccountType.Class,
            MappingAccountType.Location,
          ].includes(k)
        )
      }
    )
  )

  const categoryMapping = mappings.category?.[transaction.annotation.category] || {}

  const assetMapping =
    mappings.default?.Token && transaction.value.value.asset in mappings.default.Token
      ? { Asset: mappings.default.Token[transaction.value.value.asset] }
      : {}

  const transactionMapping = transaction.versionId
    ? (mappings.transaction?.[transaction.versionId] ?? {})
    : {}

  return {
    ...defaultMapping,
    ...fromWalletMapping,
    ...toWalletMapping,
    ...categoryMapping,
    ...assetMapping,
    ...transactionMapping,
  }
}

export function getChartOfAccountsMappingForTransaction(
  transaction: HydratedTransaction,
  mappings: AccountingMappings
) {
  const override = transaction.versionId
    ? mappings[MappingType.Transaction]?.[transaction.versionId]
    : undefined
  if (override && !isEmpty(override)) {
    return override
  } else {
    return getHierarchicalMapping(transaction, mappings)
  }
}

export type InboundOutboundActivityMappingAccount = {
  accountName: string
  splitType: MappingSplitDistributionType
  splitValue?: string
  splitAsset?: string
  splitLabel?: React.ReactNode
}

/**
 * getInboundOutboundActivityMappingAccounts
 *
 * This is a helper function that takes an InboundActivity or OutboundActivity ERP MappingAccount
 * and returns an array of InboundOutboundActivityMappingAccount objects.
 *
 * This is used for displaying and formatting distribution splits
 */
export const useGetInboundOutboundActivityMappingAccounts = (
  inboundOutboundActivity: ErpInboundOutboundActivity | undefined
) => {
  const { formatFiat, formatPercentage, formatQuantity } = useCosmicLocalizationContext()

  const fallback: InboundOutboundActivityMappingAccount = {
    accountName: 'None',
    splitValue: '',
    splitLabel: '',
    splitType: MappingSplitDistributionType.Full,
  }
  const accounts: InboundOutboundActivityMappingAccount[] = []
  if (inboundOutboundActivity && 'type' in inboundOutboundActivity) {
    switch (inboundOutboundActivity.type) {
      case MappingSplitDistributionType.Full: {
        accounts.push({
          accountName: inboundOutboundActivity.account.Name,
          splitValue: '',
          splitLabel: '',
          splitType: MappingSplitDistributionType.Full,
        })
        break
      }
      case MappingSplitDistributionType.Percent: {
        for (const split of inboundOutboundActivity.splits) {
          accounts.push({
            accountName: split.account.Name,
            splitValue: split.percent,
            splitLabel: <BodySmall>{formatPercentage(split.percent)}</BodySmall>,
            splitType: MappingSplitDistributionType.Percent,
          })
        }
        break
      }
      case MappingSplitDistributionType.Fiat: {
        for (const split of inboundOutboundActivity.splits) {
          accounts.push({
            accountName: split.account.Name,
            splitValue: split.amount,
            splitLabel: <BodySmall>{formatFiat(split.amount)}</BodySmall>,
            splitType: MappingSplitDistributionType.Fiat,
          })
        }
        break
      }
      case MappingSplitDistributionType.Token: {
        for (const split of inboundOutboundActivity.splits) {
          accounts.push({
            accountName: split.account.Name,
            splitValue: split.amount,
            splitAsset: split.asset,
            splitLabel: (
              <Flex alignItems="center" gap="xs" direction="row">
                <TokenAvatar token={split.asset} />
                <BodySmall>{formatQuantity(split.amount)}</BodySmall>
                <BodySmall>{truncateToLength(split.asset, 5)}</BodySmall>
              </Flex>
            ),
            splitType: MappingSplitDistributionType.Token,
          })
        }
        break
      }
      default: {
        break
      }
    }
  }
  if (accounts.length === 0) {
    accounts.push(fallback)
  }
  return accounts
}

// ===== VOUCHER ELIGIBILITY UTILITIES =====

/**
 * Check if a transaction or transaction group can be added to a voucher
 */
export function canAddToVoucher(transaction: HydratedTransaction): boolean {
  if (!transaction.group || transaction.group.length === 0) {
    // Single transaction - check if it's IN or OUT
    return transaction.transactionType === 'In' || transaction.transactionType === 'Out'
  }

  // Group of transactions - check if all have same asset type and transaction type
  const allTransactions = [transaction, ...transaction.group]
  const firstAsset = allTransactions[0]?.value?.value?.asset
  const firstTxnType = allTransactions[0]?.transactionType

  // Check if all transactions have the same asset type
  const sameAssetType = allTransactions.every((txn) => txn.value.value.asset === firstAsset)

  // Check if all transactions are the same type and are either 'In' or 'Out'
  const sameTxnType = allTransactions.every((txn) => txn.transactionType === firstTxnType)
  const validTxnType = firstTxnType === 'In' || firstTxnType === 'Out'

  return sameAssetType && sameTxnType && validTxnType
}

/**
 * Get tooltip message for disabled add to voucher button
 */
export function getAddToVoucherDisabledReason(
  transaction: HydratedTransaction,
  userHasVoucherAddPermission: boolean
): string {
  if (!userHasVoucherAddPermission) {
    return 'You do not have permission to add transactions to vouchers'
  }

  if (!transaction.group || transaction.group.length === 0) {
    // Single transaction
    if (transaction.transactionType !== 'In' && transaction.transactionType !== 'Out') {
      return 'Only In and Out transactions can be added to vouchers'
    }
    return ''
  }

  // Group of transactions
  const allTransactions = [transaction, ...transaction.group]
  const firstAsset = allTransactions[0]?.value?.value?.asset
  const firstTxnType = allTransactions[0]?.transactionType

  const validTxnType = firstTxnType === 'In' || firstTxnType === 'Out'
  if (!validTxnType) {
    return 'Only In and Out transaction groups can be added to vouchers'
  }

  // Check if all transactions have the same asset type
  const sameAssetType = allTransactions.every((txn) => txn.value.value.asset === firstAsset)
  if (!sameAssetType) {
    return 'All transactions in the group must have the same asset type'
  }

  // Check if all transactions are the same type and are either 'In' or 'Out'
  const sameTxnType = allTransactions.every((txn) => txn.transactionType === firstTxnType)
  if (!sameTxnType) {
    return 'All transactions in the group must be the same transaction type'
  }

  return ''
}

export function resolveVoucherForTransaction(
  transaction: HydratedTransaction,
  vouchers: VoucherListResponse | undefined
): Voucher | undefined {
  if (!transaction.voucher_info?.voucher_id) {
    return undefined
  }
  return vouchers?.vouchers.find((v) => v.id === transaction.voucher_info?.voucher_id)
}

/**
 * Check if a transaction can be removed from a voucher.
 * Uses the role/status/action permission matrix via isActionAllowed.
 */
export function canRemoveFromVoucher(
  transaction: HydratedTransaction,
  voucher: Voucher | undefined,
  isVouchersLoading: boolean,
  user: User | undefined
): boolean {
  if (isVouchersLoading || !transaction.voucher_info?.voucher_id || !user) {
    return false
  }

  return isActionAllowed(user, voucher, VoucherAction.RemoveTransactions)
}

/**
 * Get tooltip message for disabled remove from voucher button
 */
export function getRemoveFromVoucherDisabledReason(
  transaction: HydratedTransaction,
  voucher: Voucher | undefined,
  isVouchersLoading: boolean,
  user: User | undefined
): string {
  if (isVouchersLoading) {
    return 'Loading...'
  }

  if (!transaction.voucher_info?.voucher_id) {
    return 'Transaction is not associated with a voucher'
  }

  if (!isActionAllowed(user, voucher, VoucherAction.RemoveTransactions)) {
    return 'You do not have permission to remove transactions from this voucher'
  }

  return ''
}

export function convertTransactionNumbers(transaction: Transaction): Transaction {
  // Convert GainLoss fields
  if (transaction.mainGainLoss) {
    transaction.mainGainLoss = {
      ...transaction.mainGainLoss,
      costBasis: toNumber(transaction.mainGainLoss.costBasis),
      displayGainLoss: toNumber(transaction.mainGainLoss.displayGainLoss),
      calculatedGainLoss: toNumber(transaction.mainGainLoss.calculatedGainLoss),
    }
  }

  if (transaction.feeGainLoss) {
    transaction.feeGainLoss = {
      ...transaction.feeGainLoss,
      costBasis: toNumber(transaction.feeGainLoss.costBasis),
      displayGainLoss: toNumber(transaction.feeGainLoss.displayGainLoss),
      calculatedGainLoss: toNumber(transaction.feeGainLoss.calculatedGainLoss),
    }
  }

  // Convert TransactionValue fields
  transaction.value = {
    ...transaction.value,
    value: {
      ...transaction.value.value,
      amount: toNumber(transaction.value.value.amount),
    },
    transactionCost: transaction.value.transactionCost
      ? {
          ...transaction.value.transactionCost,
          amount: toNumber(transaction.value.transactionCost.amount),
        }
      : null,
    convertedValues: transaction.value.convertedValues.map((convertedValue) => ({
      ...convertedValue,
      amount: toNumber(convertedValue.amount),
    })),
    convertedCosts: transaction.value.convertedCosts.map((convertedCost) => ({
      ...convertedCost,
      amount: toNumber(convertedCost.amount),
    })),
  }

  // Convert accounting status JournalEntry fields
  if (transaction.accountingStatus) {
    transaction.accountingStatus = transaction.accountingStatus.map((status) => {
      if (status.accountEntry) {
        return {
          ...status,
          accountEntry: {
            ...status.accountEntry,
            ExchangeRate: status.accountEntry.ExchangeRate
              ? toNumber(status.accountEntry.ExchangeRate)
              : undefined,
            TotalAmt: status.accountEntry.TotalAmt
              ? toNumber(status.accountEntry.TotalAmt)
              : undefined,
          },
        }
      }
      return status
    })
  }

  // Convert top-level numeric fields
  transaction.usdValue = transaction.usdValue ? toNumber(transaction.usdValue) : undefined
  transaction.usdFee = transaction.usdFee ? toNumber(transaction.usdFee) : undefined

  // Convert nested group transactions recursively
  if (transaction.group) {
    transaction.group = transaction.group.map((groupTxn) => convertTransactionNumbers(groupTxn))
  }

  return transaction
}
